import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Users, MapPin, Shield, CalendarDays } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TOURNAMENT } from "@/data/tournament";
import { TEAM_EXTRAS } from "@/data/teamExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function TeamPage() {
  const { slug } = useParams();
  const [team, setTeam] = useState(null);
  const [status, setStatus] = useState("loading");
  const extras = TEAM_EXTRAS[slug] || {};
  const captainName = extras.captain_name || team?.captain_name || "Por anunciar";
  const displayName = extras.name || team?.name;

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/teams/${slug}`)
      .then((r) => {
        setTeam(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="team-page">
      <Navbar />
      <main className="pt-16">
        {status === "loading" && (
          <div className="min-h-[70vh] flex items-center justify-center">
            <span className="text-xs tracking-[0.3em] uppercase text-white/40 animate-pulse">
              Cargando equipo...
            </span>
          </div>
        )}

        {status === "notfound" && (
          <div className="min-h-[70vh] flex flex-col items-center justify-center gap-6 px-6" data-testid="team-not-found">
            <div className="font-display text-6xl sm:text-7xl uppercase text-white/20 text-center">
              Equipo no encontrado
            </div>
            <a
              href="/#equipos"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:border-white hover:bg-white/5 transition-all"
            >
              ← Volver a equipos
            </a>
          </div>
        )}

        {status === "ok" && team && (
          <>
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/10">
              <div className="absolute inset-0 grain pointer-events-none" />
              <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
                <motion.div
                  custom={0}
                  initial="hidden"
                  animate="show"
                  variants={fadeUp}
                  className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-white/40 mb-8"
                >
                  <a href="/#equipos" className="hover:text-[#EAB308] transition-colors" data-testid="team-breadcrumb-equipos">
                    Equipos
                  </a>
                  <span>/</span>
                  <span className="text-[#EAB308]">{displayName}</span>
                </motion.div>

                <motion.div
                  custom={1}
                  initial="hidden"
                  animate="show"
                  variants={fadeUp}
                  className="flex items-center gap-2 mb-6"
                >
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-[10px] tracking-[0.35em] uppercase text-green-400 font-bold">
                    Equipo Confirmado · {TOURNAMENT.edition}
                  </span>
                </motion.div>

                <div className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                    className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
                  >
                    {extras.crest && (
                      <img
                        src={extras.crest}
                        alt={`Escudo de ${displayName}`}
                        className="w-24 sm:w-32 lg:w-40 h-auto object-contain drop-shadow-[0_0_25px_rgba(234,179,8,0.25)] flex-shrink-0"
                        data-testid="team-crest"
                      />
                    )}
                    <h1
                      className="font-display text-[14vw] sm:text-[10vw] lg:text-[7.5rem] leading-[0.9] tracking-tight uppercase text-white"
                      data-testid="team-page-name"
                    >
                      {displayName}
                    </h1>
                  </motion.div>
                </div>

                <motion.div
                  custom={3}
                  initial="hidden"
                  animate="show"
                  variants={fadeUp}
                  className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10"
                >
                  <InfoCell
                    icon={<Users className="w-4 h-4" strokeWidth={1.5} />}
                    label="Capitán"
                    value={captainName}
                    testId="team-captain"
                  />
                  <InfoCell
                    icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}
                    label="Ciudad"
                    value={team.city}
                    testId="team-city"
                  />
                  <InfoCell
                    icon={<Shield className="w-4 h-4" strokeWidth={1.5} />}
                    label="Plantilla"
                    value={team.players_count > 0 ? `${team.players_count} jugadores` : "Por confirmar"}
                    testId="team-players"
                  />
                  <InfoCell
                    icon={<CalendarDays className="w-4 h-4" strokeWidth={1.5} />}
                    label="Debut"
                    value="25 Octubre"
                    testId="team-debut"
                  />
                </motion.div>
              </div>
            </section>

            {/* Team photo */}
            {extras.photo && (
              <section className="border-b border-white/10 bg-[#050505]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                  <div className="relative overflow-hidden border border-white/10">
                    <img
                      src={extras.photo}
                      alt={`Plantilla de ${displayName}`}
                      className="w-full max-h-[560px] object-cover"
                      data-testid="team-photo"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-6 flex items-center gap-3">
                      <div className="w-8 h-px bg-[#EAB308]" />
                      <span className="text-[10px] tracking-[0.35em] uppercase text-white/80">
                        La plantilla · {displayName}
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Venue strip */}
            <section className="border-b border-white/10 bg-[#070707]">
              <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-xs tracking-[0.3em] uppercase text-white/40">
                  Sede del torneo
                </div>
                <div className="text-sm text-white">
                  {TOURNAMENT.venue} · Tordera · Fútbol 7 · Liga + Eliminatoria
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20">
              <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/#equipos"
                  className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:border-white hover:bg-white/5 transition-all"
                  data-testid="team-back-btn"
                >
                  ← Todos los equipos
                </a>
                <Link
                  to="/inscripciones"
                  className="group inline-flex items-center justify-center gap-3 bg-[#EAB308] text-black px-8 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-all hover:-translate-y-0.5"
                  data-testid="team-inscribir-btn"
                >
                  Inscribir tu equipo
                  <span className="text-lg leading-none group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

const InfoCell = ({ icon, label, value, testId }) => (
  <div className="bg-[#0a0a0a] p-6 group hover:bg-[#0f0f0f] transition-colors" data-testid={testId}>
    <div className="flex items-center gap-2 text-white/50 group-hover:text-[#EAB308] transition-colors mb-3">
      {icon}
      <span className="text-[10px] tracking-[0.3em] uppercase">{label}</span>
    </div>
    <div className="font-display text-xl sm:text-2xl text-white uppercase tracking-tight">
      {value}
    </div>
  </div>
);
